import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../lib/apiClient';
import { AnimatedPage } from './motion';
import { useCredits } from '../hooks/useCredits';

const AVATAR_COLORS = ['#15803d', '#2563eb', '#7c3aed', '#dc2626', '#ea580c', '#0891b2', '#4f46e5', '#be185d'];

function avatarColor(name) {
  const sum = (name || '').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

function completeness(user) {
  let total = 4, filled = 0;
  if (user?.name) filled++;
  if (user?.email) filled++;
  if (user?.phone) filled++;
  if (user?.farm?.name) filled++;
  return Math.round((filled / total) * 100);
}

export default function ProfilePage({ onClose, user, token, onUserUpdate, onOpenWallet, onOpenFarm }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
  const { balance, balanceEur, loading: creditsLoading } = useCredits();
  const [name, setName] = useState(user?.name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [achievements, setAchievements] = useState([]);
  const [completenessData, setCompletenessData] = useState(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!token) return;
    Promise.allSettled([
      apiClient.get('/api/v1/aif/profile/achievements').then(r => r?.data ?? r ?? []),
      apiClient.get('/api/v1/aif/profile/completeness').then(r => r?.data ?? r ?? null),
    ]).then(([achRes, compRes]) => {
      if (achRes.status === 'fulfilled') setAchievements(achRes.value);
      if (compRes.status === 'fulfilled' && compRes.value) setCompletenessData(compRes.value);
    });
  }, [token]);

  const initials = (user?.name || 'U').split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  const color = avatarColor(user?.name);
  const pct = completeness({ ...user, name, phone });
  const farm = user?.farm;

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      alert(t('profile.avatarTooLarge', 'Max 2 MB'));
      return;
    }
    setAvatarUploading(true);
    setAvatarError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const data = await apiClient.post('/api/v1/aif/auth/avatar', formData);
      if (onUserUpdate) onUserUpdate(data?.data ?? data);
    } catch (e) {
      setAvatarError(e?.message || t('profile.avatarUploadFailed', 'Upload fehlgeschlagen'));
    }
    setAvatarUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const data = await apiClient.patch('/api/v1/aif/auth/me', { name: name.trim(), phone: phone.trim() }, { queueOnOffline: true }); // TODO(5b): confirm 3WM — no PATCH/DELETE ETag concurrency yet
      if (onUserUpdate) onUserUpdate(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch { /* ApiError */ }
    setSaving(false);
  };

  return (
    <AnimatedPage className="fixed inset-0 z-[var(--aif-z-page-fullscreen)] bg-[var(--aif-bg-page)] flex flex-col">
      <header className="flex items-center gap-3 px-4 py-3 border-b border-[var(--aif-border-default)] flex-shrink-0">
        <button onClick={onClose} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)]">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
        </button>
        <span className="text-sm font-semibold text-[var(--aif-text-primary)]">{t('menu.profile')}</span>
      </header>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-lg mx-auto px-6 py-6 space-y-6">
          {/* Avatar with upload */}
          <div className="flex flex-col items-center gap-3">
            <div className="relative">
              {user?.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name || 'Avatar'}
                  className="w-20 h-20 rounded-full object-cover"
                  data-testid="profile-avatar-img"
                />
              ) : (
                <div
                  className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold"
                  style={{ backgroundColor: color, color: 'var(--aif-text-on-primary, #fff)' }}
                  data-testid="profile-avatar-initials"
                >
                  {initials}
                </div>
              )}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={avatarUploading}
                className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-[var(--aif-color-primary)] flex items-center justify-center shadow-md hover:opacity-90 transition min-h-[44px] min-w-[44px] -mr-2 -mb-1"
                aria-label={t('profile.changeAvatar', 'Profilbild ändern')}
                data-testid="profile-avatar-upload-btn"
              >
                {avatarUploading ? (
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
                  </svg>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={handleAvatarUpload}
                className="hidden"
                data-testid="profile-avatar-input"
              />
            </div>
            <div className="text-center">
              <div className="font-semibold text-[var(--aif-text-primary)]">{user?.name || 'User'}</div>
              <div className="text-sm text-[var(--aif-text-muted)]">{user?.email}</div>
              {avatarError && <p className="text-xs text-[var(--aif-color-danger)] mt-1">{avatarError}</p>}
            </div>
          </div>

          {/* Completeness bar */}
          <div>
            <div className="flex justify-between text-xs text-[var(--aif-text-muted)] mb-1.5">
              <span>{t('profile.completeness')}</span>
              <span>{completenessData ? completenessData.score_pct : pct}%</span>
            </div>
            <div className="h-2 bg-[var(--aif-bg-elevated)] rounded-full overflow-hidden">
              <div className="h-full bg-[var(--aif-color-primary)] rounded-full transition-all" style={{ width: `${completenessData ? completenessData.score_pct : pct}%` }} />
            </div>
            {completenessData?.missing?.length > 0 && (
              <p className="text-[10px] text-[var(--aif-text-muted)] mt-1.5">
                {lang === 'de' ? 'Nächster Schritt: ' : 'Next: '}{completenessData.missing[0][`how_${lang}`]}
              </p>
            )}
          </div>

          {/* Credits summary */}
          <button
            onClick={onOpenWallet}
            className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-elevated)] transition"
            data-testid="profile-credits-row"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">⚡</span>
              <div className="text-left">
                <div className="text-sm font-medium text-[var(--aif-text-primary)]">{t('profile.creditsTitle')}</div>
                <div className="text-xs text-[var(--aif-text-muted)]">
                  {creditsLoading
                    ? '...'
                    : `${(balance ?? 0).toLocaleString(i18n.language)} Credits · €${(balanceEur ?? 0).toFixed(2)}`}
                </div>
              </div>
            </div>
            <svg className="w-4 h-4 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" />
            </svg>
          </button>

          {/* Achievements */}
          {achievements.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wider">{t('profile.achievements')}</h3>
              <div className="grid grid-cols-5 gap-2">
                {achievements.map(a => (
                  <div
                    key={a.key}
                    className={`flex flex-col items-center gap-1 py-2 rounded-xl text-center transition ${a.earned ? '' : 'opacity-30 grayscale'}`}
                    title={a[`desc_${lang}`]}
                  >
                    <span className="text-xl">{a.icon}</span>
                    <span className="text-[9px] leading-tight text-[var(--aif-text-muted)]">{a[`name_${lang}`]}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Form fields */}
          <div className="space-y-4">
            <Field label={t('profile.name')} value={name} onChange={setName} />
            <Field label={t('auth.email')} value={user?.email || ''} disabled />
            <Field label={t('profile.phone')} value={phone} onChange={setPhone} placeholder="+49 170 1234567" type="tel" />
          </div>

          {/* Farm link — tappable row to farm/Betrieb area */}
          {farm && (
            <button
              onClick={onOpenFarm}
              className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-elevated)] transition min-h-[44px]"
              data-testid="profile-farm-link"
            >
              <div className="flex items-center gap-3">
                <svg className="w-5 h-5 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>
                <div className="text-left">
                  <div className="text-sm font-medium text-[var(--aif-text-primary)]">{farm.name || t('profile.farmInfo')}</div>
                  <div className="text-xs text-[var(--aif-text-muted)]">{t('profile.editFarm', 'Betrieb bearbeiten')}</div>
                </div>
              </div>
              <svg className="w-4 h-4 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
            </button>
          )}

          {/* Password change */}
          <PasswordChangeSection t={t} />

          {/* Save button (name/phone) */}
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 text-sm font-medium rounded-2xl transition bg-[var(--aif-color-primary)] text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? t('profile.saving') : saved ? t('profile.saved') : t('profile.save')}
          </button>
        </div>
      </div>
    </AnimatedPage>
  );
}

function Field({ label, value, onChange, disabled, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[var(--aif-text-muted)] mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange ? (e) => onChange(e.target.value) : undefined}
        disabled={disabled}
        placeholder={placeholder}
        className={`w-full px-4 py-3 text-sm rounded-xl border border-[var(--aif-border-default)] bg-[var(--aif-bg-surface)] text-[var(--aif-text-primary)] placeholder:text-[var(--aif-text-muted)] focus:outline-none focus:border-[var(--aif-border-focus)] transition ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
      />
    </div>
  );
}

function PasswordChangeSection({ t }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState(null); // 'saving' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async () => {
    if (next.length < 8) {
      setErrorMsg(t('profile.pwMinLength', 'Mindestens 8 Zeichen'));
      setStatus('error');
      return;
    }
    if (next !== confirm) {
      setErrorMsg(t('profile.pwMismatch', 'Passwörter stimmen nicht überein'));
      setStatus('error');
      return;
    }
    setStatus('saving');
    setErrorMsg('');
    try {
      await apiClient.post('/api/v1/aif/auth/change-password', {
        current_password: current,
        new_password: next,
      });
      setStatus('success');
      setCurrent('');
      setNext('');
      setConfirm('');
      setTimeout(() => { setStatus(null); setOpen(false); }, 2000);
    } catch (e) {
      setErrorMsg(e?.message || t('profile.pwChangeFailed', 'Passwort-Änderung fehlgeschlagen'));
      setStatus('error');
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="w-full flex items-center justify-between px-4 py-3 rounded-2xl bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-elevated)] transition min-h-[44px]"
        data-testid="profile-pw-change-btn"
      >
        <div className="flex items-center gap-3">
          <svg className="w-5 h-5 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
          </svg>
          <span className="text-sm font-medium text-[var(--aif-text-primary)]">{t('profile.changePassword', 'Passwort ändern')}</span>
        </div>
        <svg className="w-4 h-4 text-[var(--aif-text-muted)]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
      </button>
    );
  }

  return (
    <div className="bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-2xl p-4 space-y-3" data-testid="profile-pw-form">
      <h3 className="text-xs font-semibold text-[var(--aif-text-muted)] uppercase tracking-wider">{t('profile.changePassword', 'Passwort ändern')}</h3>
      <Field label={t('profile.currentPassword', 'Aktuelles Passwort')} value={current} onChange={setCurrent} type="password" />
      <Field label={t('profile.newPassword', 'Neues Passwort')} value={next} onChange={setNext} type="password" />
      <Field label={t('profile.confirmPassword', 'Passwort bestätigen')} value={confirm} onChange={setConfirm} type="password" />
      {status === 'error' && <p className="text-xs text-[var(--aif-color-danger)]">{errorMsg}</p>}
      {status === 'success' && <p className="text-xs text-[var(--aif-color-success)]">{t('profile.pwChanged', 'Passwort geändert')}</p>}
      <div className="flex gap-2">
        <button onClick={() => { setOpen(false); setStatus(null); }} className="flex-1 py-2.5 text-sm rounded-xl bg-[var(--aif-bg-elevated)] text-[var(--aif-text-secondary)] min-h-[44px]">
          {t('procurement.cancel', 'Abbrechen')}
        </button>
        <button
          onClick={handleSubmit}
          disabled={status === 'saving' || !current || !next || !confirm}
          className="flex-1 py-2.5 text-sm font-medium rounded-xl bg-[var(--aif-color-primary)] text-white disabled:opacity-50 min-h-[44px]"
          data-testid="profile-pw-submit"
        >
          {status === 'saving' ? '...' : t('profile.save', 'Speichern')}
        </button>
      </div>
    </div>
  );
}

